"use client"

import * as SliderPrimitive from "@radix-ui/react-slider"
import React, { JSX, memo, useCallback } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps, cn } from "../../utils"

/**
 * @description 중앙 기준 슬라이더. 중앙값(0)을 기준으로 좌우로 조절
 * @example
 * ```tsx
 * <TCenterSlider value={contrast} min={-100} max={100} onChange={setContrast} />
 * ```
 */
interface TCenterSliderProps extends BaseInteractiveProps {
	/** 슬라이더 input name 속성 */
	name?: string
	/** 슬라이더 값. TSlider와 달리 단일 숫자 */
	value: number
	/**
	 * 최솟값
	 * @default -100
	 */
	min?: number
	/**
	 * 최댓값
	 * @default 100
	 */
	max?: number
	/**
	 * 값 변경 단위
	 * @default 1
	 */
	step?: number
	/** 왼쪽 라벨 */
	leftLabel?: React.ReactNode
	/** 오른쪽 라벨 */
	rightLabel?: React.ReactNode
	/**
	 * 라벨 배치 위치
	 * @default "side"
	 * @ai-style "side" - 슬라이더 양 옆에 라벨 배치
	 * @ai-style "bottom" - 슬라이더 하단에 라벨 배치
	 */
	labelPlacement?: "side" | "bottom"
	/** 값 변경 콜백. 단일 숫자를 전달 */
	onChange: (value: number) => void
	/** 드래그 완료(커밋) 시 콜백. 슬라이더에서 손을 뗄 때 최종 값 전달 */
	onValueCommit?: (value: number) => void
}

function TCenterSliderInner(props: TCenterSliderProps): JSX.Element {
	const {
		id,
		name,
		value,
		min = -100,
		max = 100,
		step = 1,
		leftLabel,
		rightLabel,
		labelPlacement = "side",
		disabled,
		className,
		dataAttributes,
		onChange,
		onValueCommit,
		...restProps
	} = props

	const dataProps = useDataAttributes(dataAttributes)

	// 슬라이더 값 변경 핸들러
	const handleValueChange = useCallback(
		(newValue: number[]) => {
			onChange(newValue[0] ?? 0)
		},
		[onChange]
	)

	const handleValueCommit = useCallback(
		(newValue: number[]) => {
			onValueCommit?.(newValue[0] ?? 0)
		},
		[onValueCommit]
	)

	// 0을 기준으로 정규화된 값 계산 (0~1 범위)
	const { centerPosition, rangeLeft, rangeWidth } = React.useMemo(() => {
		const normalized = (value - min) / (max - min)
		const positive = value >= 0
		const center = (0 - min) / (max - min)
		const left = positive ? center : normalized
		const width = positive ? normalized - center : center - normalized

		return {
			centerPosition: center,
			rangeLeft: left,
			rangeWidth: width
		}
	}, [value, min, max])

	const slider = (
		<div className="center-slider">
			<div className="slider-container">
				<SliderPrimitive.Root
					id={id}
					name={name}
					value={[value]}
					min={min}
					max={max}
					step={step}
					disabled={disabled}
					onValueChange={handleValueChange}
					onValueCommit={handleValueCommit}
					className={cn("relative flex w-full touch-none items-center select-none", labelPlacement === "side" ? "grow" : "w-full")}
					{...dataProps}
				>
					<SliderPrimitive.Track
						className={cn("bg-black/20 relative grow overflow-hidden rounded-full h-1", "dark:bg-white/20", "data-disabled:bg-g200 dark:data-disabled:bg-g200")}
					>
						<div
							className={cn("absolute h-full", "bg-p500", "data-disabled:bg-g400")}
							style={{
								left: `${rangeLeft * 100}%`,
								width: `${rangeWidth * 100}%`
							}}
						/>
					</SliderPrimitive.Track>
					<div
						className="center-divider"
						style={{
							left: `${centerPosition * 100}%`
						}}
					/>
					<SliderPrimitive.Thumb
						data-slot="slider-thumb"
						data-value={value}
						className={cn(
							"bg-white block border border-g100 ring-ring/15 size-4 shrink-0 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.2)] cursor-pointer transition-[color,box-shadow]",
							"relative hover:ring-5 focus:ring-8 focus:outline-hidden",
							"after:content-[attr(data-value)] after:absolute after:-top-6 after:left-1/2 after:-translate-x-1/2 after:px-2 after:h-5 after:flex after:items-center after:justify-center after:rounded-full after:bg-g200 after:border after:border-border after:text-text after:text-xs after:font-medium after:opacity-0 after:transition-opacity after:pointer-events-none",
							"hover:after:opacity-100",
							"data-disabled:pointer-events-none data-disabled:after:bg-g400"
						)}
					/>
				</SliderPrimitive.Root>
			</div>
		</div>
	)

	return (
		<div className={cn("TCenterSlider", className)} {...restProps} data-label-placement={labelPlacement}>
			{labelPlacement === "side" ? (
				<div className="side">
					{leftLabel && <span className="label">{leftLabel}</span>}
					{slider}
					{rightLabel && <span className="label">{rightLabel}</span>}
				</div>
			) : (
				<>
					{slider}
					<div className="bottom">
						{leftLabel && <span className="label">{leftLabel}</span>}
						{rightLabel && <span className="label">{rightLabel}</span>}
					</div>
				</>
			)}
		</div>
	)
}

TCenterSliderInner.displayName = "TCenterSlider"

export const TCenterSlider = memo(TCenterSliderInner)
